import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { BookOpen, Video, FileText, Calendar, Download, ArrowRight, Clock, User } from 'lucide-react';
import { blogPosts } from '../data/mockData';

const ResourcesPage = () => {
  const resourceCategories = [
    {
      title: "Product Demos",
      description: "Interactive demos and product walkthroughs",
      icon: Video,
      items: [
        { title: "AI Lead Intelligence Demo", duration: "15 min", type: "Interactive Demo" },
        { title: "Automation Rules Tutorial", duration: "12 min", type: "Video Guide" },
        { title: "Forecasting Features Overview", duration: "10 min", type: "Product Demo" }
      ]
    },
    {
      title: "Documentation",
      description: "Comprehensive guides and API documentation",
      icon: FileText,
      items: [
        { title: "Getting Started Guide", pages: "25 pages", type: "PDF Guide" },
        { title: "API Documentation", pages: "150+ endpoints", type: "Technical Docs" },
        { title: "Best Practices Handbook", pages: "40 pages", type: "Strategy Guide" }
      ]
    },
    {
      title: "Webinars & Events",
      description: "Live sessions and recorded webinars",
      icon: Calendar,
      items: [
        { title: "Monthly Product Updates", schedule: "Every 2nd Tuesday", type: "Live Webinar" },
        { title: "Sales Strategy Sessions", schedule: "Weekly", type: "Training Session" },
        { title: "Customer Success Stories", schedule: "Monthly", type: "Case Study Review" }
      ]
    },
    {
      title: "Downloads",
      description: "Templates, checklists, and tools",
      icon: Download,
      items: [
        { title: "CRM Implementation Checklist", format: "PDF", type: "Checklist" },
        { title: "Sales Process Templates", format: "Excel", type: "Templates" },
        { title: "ROI Calculator", format: "Interactive Tool", type: "Calculator" }
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center animate-fade-in">
            <h1 className="display-lg text-white mb-6">
              Resources & Learning Center
            </h1>
            <p className="body-lg mb-8 max-w-2xl mx-auto">
              Everything you need to succeed with GrowSquare. From getting started guides to advanced strategies, 
              explore our comprehensive resource library.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/demo">
                <Button className="btn-primary glow-effect">
                  Request Personal Demo
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Button className="btn-secondary">
                <BookOpen className="mr-2 h-5 w-5" />
                Browse All Resources
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Resource Categories */}
      <section className="section-padding">
        <div className="container">
          <div className="text-center mb-16">
            <Badge className="bg-green-400/10 text-green-400 mb-4">Learning Resources</Badge>
            <h2 className="display-md text-white mb-6">
              Learn, Implement, Succeed
            </h2>
            <p className="body-lg max-w-3xl mx-auto">
              Access our comprehensive library of resources designed to help you maximize your success with GrowSquare.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {resourceCategories.map((category, index) => {
              const IconComponent = category.icon;
              
              return (
                <Card key={index} className="feature-card hover-card">
                  <CardHeader>
                    <div className="w-12 h-12 rounded-lg bg-green-400/10 flex items-center justify-center mb-4">
                      <IconComponent className="h-6 w-6 text-green-400" />
                    </div>
                    <CardTitle className="h3 text-white">{category.title}</CardTitle>
                    <CardDescription className="body-md">
                      {category.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4 mb-6">
                      {category.items.map((item, itemIndex) => (
                        <div key={itemIndex} className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                          <div>
                            <p className="body-sm font-medium text-white">{item.title}</p>
                            <p className="body-sm text-slate-400">{item.type}</p>
                          </div>
                          <div className="text-right">
                            <p className="body-sm text-green-400">
                              {item.duration || item.pages || item.schedule || item.format}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <Button className="btn-secondary w-full">
                      Explore {category.title}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="section-padding section-dark">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="display-md text-white mb-6">
              Latest from Our Blog
            </h2>
            <p className="body-lg max-w-2xl mx-auto">
              Stay updated with the latest insights, best practices, and industry trends in CRM and sales automation.
            </p>
          </div>

          <div className="card-grid-3">
            {blogPosts.map((post,i) => (
              <Card key={i} className="feature-card hover-card">
                <CardHeader>
                  <Badge className="bg-green-400/10 text-green-400 w-fit mb-2">
                    {post.category}
                  </Badge>
                  <CardTitle className="h3 text-white">{post.title}</CardTitle>
                  <CardDescription className="body-md">
                    {post.excerpt}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-slate-400 text-sm mb-4">
                    <div className="flex items-center space-x-2">
                      <User className="h-4 w-4" />
                      <span>{post.author}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4" />
                      <span>{post.readTime}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="body-sm text-slate-400">
                      {new Date(post.date).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </span>
                    <Button className="btn-secondary">
                      Read More
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button className="btn-primary">
              View All Articles
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Help & Support */}
      <section className="section-padding">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="display-md text-white mb-6">
              Need Help Getting Started?
            </h2>
            <p className="body-lg max-w-2xl mx-auto">
              Our support team is here to help you succeed. Choose the support option that works best for you.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="feature-card text-center">
              <CardContent className="pt-8">
                <div className="w-16 h-16 bg-green-400/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Video className="h-8 w-8 text-green-400" />
                </div>
                <h3 className="h3 text-white mb-4">Personal Demo</h3>
                <p className="body-md mb-6">
                  Get a customized demo tailored to your specific business needs and use cases.
                </p>
                <Link to="/demo">
                  <Button className="btn-primary w-full">
                    Schedule Demo
                  </Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="feature-card text-center">
              <CardContent className="pt-8">
                <div className="w-16 h-16 bg-green-400/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <BookOpen className="h-8 w-8 text-green-400" />
                </div>
                <h3 className="h3 text-white mb-4">Knowledge Base</h3>
                <p className="body-md mb-6">
                  Browse our comprehensive documentation and step-by-step guides.
                </p>
                <Button className="btn-secondary w-full">
                  Browse Docs
                </Button>
              </CardContent>
            </Card>

            <Card className="feature-card text-center">
              <CardContent className="pt-8">
                <div className="w-16 h-16 bg-green-400/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Calendar className="h-8 w-8 text-green-400" />
                </div>
                <h3 className="h3 text-white mb-4">Expert Consultation</h3>
                <p className="body-md mb-6">
                  Book a one-on-one consultation with our CRM implementation experts.
                </p>
                <Link to="/contact">
                  <Button className="btn-secondary w-full">
                    Book Consultation
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding section-dark">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="display-md text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="body-lg mb-8 max-w-2xl mx-auto">
              Have questions or need personalized guidance? Our team of experts is ready to help you 
              implement GrowSquare successfully.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/demo">
                <Button className="btn-primary glow-effect">
                  Request Demo
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button className="btn-secondary">
                  Contact Support
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
export default ResourcesPage;